import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import {
    Box,
    IconButton,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    styled,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import React, { useMemo, useState } from "react";
import {
    BadgeStatusCustomer,
    FormatCurrency,
    TableText,
} from "../../../components/template/partials";
import { useCustomerUIContext } from "../CustomerUIContext";

const StyledTable = styled(Table)(() => ({
    width: "max-content",
    whiteSpace: "pre",
    "& thead": {
        "& tr": { "& th": { paddingLeft: 0, paddingRight: 0 } },
    },
    "& tbody": {
        "& tr": {
            "& td": {
                paddingLeft: 0,
                paddingTop: "2px",
                paddingBottom: 0,
            },
        },
    },
}));

function descendingComparator(a, b, orderBy) {
    if (orderBy === "waktu_pembersihan") {
        // Mengonversi nilai waktu menjadi objek Date
        const dateA = a[orderBy] ? new Date(a[orderBy]) : new Date(0);
        const dateB = b[orderBy] ? new Date(b[orderBy]) : new Date(0);

        // Melakukan perbandingan berdasarkan waktu
        if (dateB < dateA) {
            return -1;
        }
        if (dateB > dateA) {
            return 1;
        }
        return 0;
    } else {
        let dataA;
        let dataB;
        if (orderBy === "nama_pelanggan") {
            dataA = a[orderBy].toLowerCase();
            dataB = b[orderBy].toLowerCase();
        } else {
            dataA = a[orderBy];
            dataB = b[orderBy];
        }

        if (dataB < dataA) {
            return -1;
        }
        if (dataB > dataA) {
            return 1;
        }
        return 0;
    }
}

function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

export function EnhancedTableHead(props) {
    const { order, orderBy, onRequestSort } = props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };
    return (
        <TableHead className="table-header">
            <TableRow
                sx={{
                    "& .MuiTableSortLabel-icon": {
                        color: "white !important",
                        opacity: 0.3,
                    },
                }}
            >
                <TableCell
                    className="table-content-xs text-white "
                    align="center"
                >
                    No
                </TableCell>
                <TableCell
                    className="table-content-sm text-white"
                    align="center"
                    key="nama_pelanggan"
                    sortDirection={orderBy === "nama_pelanggan" ? order : false}
                >
                    <TableSortLabel
                        style={{ color: "white" }}
                        active={orderBy === "nama_pelanggan"}
                        direction={orderBy === "nama_pelanggan" ? order : "asc"}
                        onClick={createSortHandler("nama_pelanggan")}
                    >
                        Nama Pelanggan
                        {orderBy === "nama_pelanggan" ? (
                            <Box component="span" sx={visuallyHidden}>
                                {order === "desc"
                                    ? "sorted descending"
                                    : "sorted ascending"}
                            </Box>
                        ) : null}
                    </TableSortLabel>
                </TableCell>
                <TableCell
                    className="table-content-md text-white"
                    align="center"
                    key="status"
                    sortDirection={orderBy === "status" ? order : false}
                >
                    <TableSortLabel
                        style={{ color: "white" }}
                        active={orderBy === "status"}
                        direction={orderBy === "status" ? order : "asc"}
                        onClick={createSortHandler("status")}
                    >
                        Status
                        {orderBy === "status" ? (
                            <Box component="span" sx={visuallyHidden}>
                                {order === "desc"
                                    ? "sorted descending"
                                    : "sorted ascending"}
                            </Box>
                        ) : null}
                    </TableSortLabel>
                </TableCell>
                <TableCell
                    className="table-content-sm text-white"
                    style={{
                        overflowWrap: "break-word",
                        wordBreak: "break-word",
                    }}
                    align="center"
                    key="waktu_pembersihan"
                    sortDirection={
                        orderBy === "waktu_pembersihan" ? order : false
                    }
                >
                    <TableSortLabel
                        style={{ color: "white" }}
                        active={orderBy === "waktu_pembersihan"}
                        direction={
                            orderBy === "waktu_pembersihan" ? order : "asc"
                        }
                        onClick={createSortHandler("waktu_pembersihan")}
                    >
                        Waktu Penyedotan
                        {orderBy === "waktu_pembersihan" ? (
                            <Box component="span" sx={visuallyHidden}>
                                {order === "desc"
                                    ? "sorted descending"
                                    : "sorted ascending"}
                            </Box>
                        ) : null}
                    </TableSortLabel>
                </TableCell>
                <TableCell
                    className="table-content-sm text-white"
                    align="center"
                >
                    No. Telepon
                </TableCell>
                <TableCell
                    className="table-content-md text-white"
                    align="center"
                >
                    Alamat
                </TableCell>
                <TableCell
                    className="table-content-sm text-white"
                    align="center"
                >
                    Jenis Bangunan
                </TableCell>
                <TableCell
                    className="table-content-xs text-white"
                    align="center"
                    style={{
                        overflowWrap: "break-word",
                        wordBreak: "break-word",
                    }}
                >
                    Kapasitas Tangki Septik
                </TableCell>
                <TableCell
                    className="table-content-sm text-white"
                    align="center"
                    style={{
                        overflowWrap: "break-word",
                        wordBreak: "break-word",
                    }}
                >
                    Jumlah Penghuni (Jiwa)
                </TableCell>

                <TableCell
                    className="table-content-md text-white"
                    align="center"
                    key="tagihan"
                    sortDirection={orderBy === "tagihan" ? order : false}
                >
                    <TableSortLabel
                        style={{ color: "white" }}
                        active={orderBy === "tagihan"}
                        direction={orderBy === "tagihan" ? order : "asc"}
                        onClick={createSortHandler("tagihan")}
                    >
                        Tagihan
                        {orderBy === "tagihan" ? (
                            <Box component="span" sx={visuallyHidden}>
                                {order === "desc"
                                    ? "sorted descending"
                                    : "sorted ascending"}
                            </Box>
                        ) : null}
                    </TableSortLabel>
                </TableCell>
                <TableCell
                    className="table-content-xs text-white"
                    align="center"
                    style={{
                        overflowWrap: "break-word",
                        wordBreak: "break-word",
                    }}
                >
                    Volume yang Disedot
                </TableCell>
                <TableCell
                    className="table-content-xs text-white"
                    align="center"
                >
                    Action
                </TableCell>
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propsTypes = {
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.oneOf(["asc", "desc"]).isRequired,
    orderBy: PropTypes.string.isRequired,
};

export function CustomerListTable() {
    const customerUIContext = useCustomerUIContext();
    const accountUIProps = useMemo(() => {
        return {
            getListCustomerData: customerUIContext.getListCustomerData,
            goToCustomerDetail: customerUIContext.goToCustomerDetail,
            goToCustomerEdit: customerUIContext.goToCustomerEdit,
            goToDeleteCustomer: customerUIContext.goToDeleteCustomer,
            goToSchedule: customerUIContext.goToSchedule,
        };
    }, [customerUIContext]);
    const {
        getListCustomerData,
        goToCustomerDetail,
        goToCustomerEdit,
        goToDeleteCustomer,
        goToSchedule,
    } = accountUIProps;

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [anchorEl, setAnchorEl] = useState(null);
    const [itemDetail, setItemDetail] = useState(null);

    const handleClickMoreAction = (event, item) => {
        setAnchorEl(event.currentTarget);
        setItemDetail(item);
    };

    const handleCloseMoreAction = () => {
        setAnchorEl(null);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const [order, setOrder] = React.useState("asc");
    const [orderBy, setOrderBy] = React.useState("nama_pelanggan");

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleClickMenu = (key) => {
        const data = itemDetail;
        switch (key) {
            case "customer_detail":
                goToCustomerDetail(data.id);
                break;
            case "customer_edit":
                goToCustomerEdit(data.id);
                break;
            case "customer_delete":
                goToDeleteCustomer(data.id, data);
                break;
            case "schedule_customer":
                goToSchedule(data.waktu_pembersihan);
                break;
            default:
                break;
        }
    };

    const moreActionMenuItems = [
        {
            key: "customer_detail",
            label: "Detail Pelanggan",
        },
        {
            key: "customer_edit",
            label: "Edit Pelanggan",
        },
        {
            key: "customer_delete",
            label: "Delete Pelanggan",
        },
        {
            key: "schedule_customer",
            label: "Jadwal Pelanggan",
        },
    ];

    return (
        <Box width="100%" overflow="auto">
            <TableContainer>
                <StyledTable className="table-container">
                    <EnhancedTableHead
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                    />
                    <TableBody style={{ padding: "2px" }}>
                        {getListCustomerData.isFulfilled &&
                            !getListCustomerData.isLoading &&
                            getListCustomerData.data &&
                            stableSort(
                                getListCustomerData.data,
                                getComparator(order, orderBy)
                            )
                                .slice(
                                    page * rowsPerPage,
                                    page * rowsPerPage + rowsPerPage
                                )
                                .map((item, index) => (
                                    <TableRow hover key={item.id}>
                                        <TableCell
                                            className="table-content-xs"
                                            align="center"
                                        >
                                            {TableText(
                                                index + 1 + page * rowsPerPage
                                            )}
                                        </TableCell>
                                        <TableCell
                                            className="table-content-md"
                                            align="center"
                                        >
                                            {TableText(item.nama_pelanggan)}
                                        </TableCell>
                                        <TableCell
                                            className="table-content-md"
                                            align="center"
                                        >
                                            {BadgeStatusCustomer(item.status)}
                                        </TableCell>
                                        <TableCell
                                            className="table-content-lg"
                                            align="center"
                                        >
                                            {item.waktu_pembersihan
                                                ? dayjs(item.waktu_pembersihan)
                                                      .locale("id")
                                                      .format(
                                                          "dddd, DD MMMM YYYY [Pukul] HH:mm"
                                                      )
                                                : TableText(
                                                      item.waktu_pembersihan
                                                  )}
                                        </TableCell>
                                        <TableCell
                                            className="table-content-sm"
                                            align="center"
                                        >
                                            {TableText(item.nomor_hp)}
                                        </TableCell>
                                        <TableCell
                                            className="table-content-md"
                                            align="justify"
                                        >
                                            {TableText(item.alamat_detail)}
                                        </TableCell>
                                        <TableCell
                                            className="table-content-sm"
                                            align="center"
                                        >
                                            {TableText(item.jenis_bangunan)}
                                        </TableCell>
                                        <TableCell
                                            className="table-content-sm"
                                            align="center"
                                        >
                                            {TableText(
                                                item.volume_tangki_septik
                                            )}
                                        </TableCell>
                                        <TableCell
                                            className="table-content-xs"
                                            align="center"
                                        >
                                            {TableText(item.jumlah_penghuni)}
                                        </TableCell>
                                        <TableCell
                                            className="table-content-md"
                                            align="center"
                                        >
                                            {FormatCurrency(item.tagihan)}
                                        </TableCell>
                                        <TableCell
                                            className="table-content-sm"
                                            align="center"
                                        >
                                            {TableText(item.volume_disedot)}
                                        </TableCell>
                                        <TableCell
                                            className="table-content-xs"
                                            align="center"
                                        >
                                            <IconButton
                                                color="inherit"
                                                aria-label="Vertical Ellipsis"
                                                onClick={(event) =>
                                                    handleClickMoreAction(
                                                        event,
                                                        item
                                                    )
                                                }
                                            >
                                                <MoreVertIcon />
                                            </IconButton>
                                            <Menu
                                                anchorEl={anchorEl}
                                                open={Boolean(anchorEl)}
                                                onClose={handleCloseMoreAction}
                                            >
                                                {moreActionMenuItems?.map(
                                                    (
                                                        itemMoreAction,
                                                        indexMoreAction
                                                    ) => (
                                                        <MenuItem
                                                            key={
                                                                indexMoreAction
                                                            }
                                                            onClick={() =>
                                                                handleClickMenu(
                                                                    itemMoreAction.key
                                                                )
                                                            }
                                                        >
                                                            {
                                                                itemMoreAction.label
                                                            }
                                                        </MenuItem>
                                                    )
                                                )}
                                            </Menu>
                                        </TableCell>
                                    </TableRow>
                                ))}
                    </TableBody>
                </StyledTable>
            </TableContainer>
            <TablePagination
                component="div"
                rowsPerPage={rowsPerPage}
                page={page}
                count={getListCustomerData.count}
                onPageChange={handleChangePage}
                rowsPerPageOptions={[5, 10, 20]}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Box>
    );
}
